import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(isInStandaloneMode);
    
    if (isInStandaloneMode) {
      return; // Already installed, don't show prompt
    }
    
    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);
    
    // Check if user dismissed previously
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Show again after 7 days
    if (dismissed && daysSinceDismissed < 7) {
      return;
    }
    
    // Listen for install prompt (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 30 seconds on the page
      setTimeout(() => {
        setShowPrompt(true);
      }, 30000);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // For iOS, show custom instructions after delay
    if (iOS) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 30000);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User ${outcome} the install prompt`);
    
    // Clear the prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    setShowPrompt(false);
  };

  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-green to-emerald-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold">App installieren</h3>
                <p className="text-sm text-white/80">Schneller Zugriff</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS Instructions
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Installieren Sie die App für schnellen Zugriff:
              </p>
              <ol className="text-sm text-gray-600 space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Tippen Sie auf das <strong>Teilen</strong>-Symbol
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Wählen Sie <strong>"Zum Home-Bildschirm"</strong>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Tippen Sie auf <strong>"Hinzufügen"</strong>
                </li>
              </ol>
            </div>
          ) : (
            // Standard install prompt
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Installieren Sie die Partner-App für schnellen Zugriff auf Ihre Fälle und Provisionen.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={handleInstall}
                  className="flex-1 gap-2 bg-brand-green hover:bg-brand-green/90"
                >
                  <Download className="w-4 h-4" />
                  Installieren
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDismiss}
                >
                  Später
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Features */}
        <div className="px-4 pb-4">
          <div className="flex gap-4 text-xs text-gray-500">
            <span>✓ Offline-Zugriff</span>
            <span>✓ Push-Benachrichtigungen</span>
            <span>✓ Schneller Start</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
